import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Grid, Typography } from '@mui/material';
import {
    ChevronLeft as ChevronLeftIcon,
    Info as InfoIcon
} from '@mui/icons-material';

import { Fetch, Header } from '@commonComponents';
import { Container } from '@styledComponents';
import { getTenantProfilePublic, recommendTenant } from '@api/tenants';
import type { FetchComponentPropsType } from '@commonComponents/Fetch/Fetch';
import { TenantProfileType } from '@types';
import { nonCarrierName, websiteUrl } from '@env';
import { goBackOrHome } from '../../utils';

import { ProfileCommon } from './TenantProfileCommon';
import RecommendResultBanner from './RecommendResultBanner';
import type { RecommendKind, RecommendState } from './RecommendResultBanner';

const recommendInFlight = new Set<string>();
const recommendCache = new Map<string, RecommendState>();
const recommendListeners = new Set<(tenantId: string) => void>();

function writeRecommendState(tenantId: string, state: RecommendState) {
    recommendCache.set(tenantId, state);
    recommendListeners.forEach((listener) => listener(tenantId));
}

function kindFromError(error: unknown): RecommendKind {
    const message = error instanceof Error ? error.message : String(error ?? '');
    if (message.includes('You cannot recommend your own company.')) return 'self';
    if (message.includes('Only verified companies can be recommended.')) return 'unverified';
    return 'error';
}

function revalidateWebsiteCompanyProfile(slug: string | null | undefined) {
    if (!slug || !websiteUrl) return;
    const base = String(websiteUrl).replace(/\/$/, '');
    fetch(`${base}/api/foretag/${encodeURIComponent(slug)}/revalidate`, {
        method: 'POST',
        keepalive: true
    }).catch(() => undefined);
}

function BuyerProfileNotice({ name }: { name: string }) {
    const navigate = useNavigate();

    return (
        <Grid
            container
            sx={{ height: '100%' }}
            justifyContent="space-around"
            alignItems="center"
        >
            <Grid item sx={{ m: 1, maxWidth: 560 }}>
                <div style={{ textAlign: 'center', marginBottom: 10 }}>
                    <InfoIcon color="action" sx={{ fontSize: 40 }} />
                </div>
                <Typography gutterBottom variant="h5" sx={{ textAlign: 'center' }}>
                    {name}
                </Typography>
                <Typography gutterBottom variant="body1" sx={{ textAlign: 'center' }}>
                    {`${name} är en ${nonCarrierName} och har ingen företagsprofil.`}
                </Typography>
                <div style={{ textAlign: 'center', marginTop: 20 }}>
                    <Button
                        variant="outlined"
                        startIcon={<ChevronLeftIcon />}
                        onClick={() => goBackOrHome(navigate)}
                        sx={{ m: 1 }}
                    >
                        Gå tillbaka
                    </Button>
                </div>
            </Grid>
        </Grid>
    );
}

function PublicProfileBody(props: FetchComponentPropsType<TenantProfileType>) {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [recommendState, setRecommendState] = React.useState<RecommendState | null>(
        () => (id ? recommendCache.get(id) ?? null : null)
    );
    const { entity, refetch } = props;

    React.useEffect(() => {
        const listener = (tenantId: string) => {
            if (tenantId === id) {
                setRecommendState(recommendCache.get(tenantId) ?? null);
            }
        };
        recommendListeners.add(listener);
        if (id) {
            [...recommendCache.keys()].forEach((key) => {
                if (key !== id) recommendCache.delete(key);
            });
            if (recommendCache.has(id)) {
                setRecommendState(recommendCache.get(id) ?? null);
            }
        }
        return () => {
            recommendListeners.delete(listener);
        };
    }, [id]);

    React.useEffect(() => {
        if (!entity.isCarrier) return undefined;
        if (!id || searchParams.get('recommend') !== 'true') return undefined;
        if (recommendInFlight.has(id)) {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete('recommend');
                return next;
            }, { replace: true });
            return undefined;
        }

        recommendInFlight.add(id);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('recommend');
            return next;
        }, { replace: true });

        (async () => {
            try {
                const data = await recommendTenant(id);
                const slug = data.slug ?? entity.slug;
                writeRecommendState(id, {
                    kind: data.status === 'already_exists' ? 'already_exists' : 'created',
                    name: data.name || entity.name,
                    slug
                });
                if (data.status === 'created') {
                    revalidateWebsiteCompanyProfile(slug);
                }
                refetch();
            } catch (error) {
                writeRecommendState(id, {
                    kind: kindFromError(error),
                    name: entity.name,
                    slug: entity.slug
                });
            } finally {
                recommendInFlight.delete(id);
            }
        })();

        return undefined;
    }, [id, searchParams, setSearchParams, entity.name, entity.slug, entity.isCarrier, refetch]);

    if (entity.isCarrier === false) {
        return <BuyerProfileNotice name={entity.name} />;
    }

    return (
        <>
            {recommendState && <RecommendResultBanner state={recommendState} />}
            <ProfileCommon {...props} />
        </>
    );
}

export default function ProfilePublic() {
    const { id } = useParams();
    const fetch = React.useCallback(() => (id ? getTenantProfilePublic(id) : undefined), [id]);

    return (
        <Container>
            <Header
                title="Företagsprofil"
            />
            <Fetch
                fetch={fetch}
                name="public"
                Component={PublicProfileBody}
            />
        </Container>
    );
}
